import { useContext, useEffect, useRef, useState } from 'react';

import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';

import buildHeartbeatQuery from './buildHeartbeatQuery';
import {
  MANIFEST_POLL_MS,
  MANIFEST_RETRY_MS,
  WALLBOARD_MANIFEST_PATH,
} from './constants';
import pickManifest from './pickManifest';

export type ManifestState =
  | { status: 'loading' }
  // No screenshots yet: the first render has been requested.
  | { status: 'preparing' }
  // The wallboard was deleted, or this TV's session was ended.
  | { status: 'removed' }
  // Never reached the endpoint.
  | { status: 'unavailable' }
  | { status: 'ready'; manifest: WallboardScreenshots.ManifestResponse };

/**
 * Polls GET /v1/wallboards/manifest. Once a manifest has loaded, a failed poll
 * keeps playing it (screenshots stay viewable until their signed URLs expire).
 * A single 401 is retried (the SDK refreshes the token meanwhile): only a
 * second in a row, or a 404, means the TV was removed.
 *
 * Each poll says what's on screen (buildHeartbeatQuery), for the TV's
 * heartbeat.
 */
const useWallboardManifest = ({
  getShownSlideId,
}: {
  // The slide on screen when the poll goes out
  getShownSlideId?: () => string | undefined;
} = {}): ManifestState => {
  const { api } = useContext(CloudFunctionClientContext);
  const [state, setState] = useState<ManifestState>({ status: 'loading' });
  // Read at each poll, so a new callback doesn't restart polling
  const getShownSlideIdRef = useRef(getShownSlideId);
  getShownSlideIdRef.current = getShownSlideId;

  useEffect(() => {
    let isActive = true;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let isAfterUnauthorized = false;
    // The state on screen, as set below (for the heartbeat)
    let shownStatus: ManifestState['status'] = 'loading';

    const poll = async () => {
      const query = buildHeartbeatQuery({
        player: 'app',
        status: shownStatus,
        slideId:
          shownStatus === 'ready' ? getShownSlideIdRef.current?.() : undefined,
      });
      const response = await api.get<WallboardScreenshots.ManifestResponse>(
        `${WALLBOARD_MANIFEST_PATH}${query}`,
      );
      if (!isActive) {
        return;
      }

      let nextPollMs = MANIFEST_POLL_MS;
      const isUnauthorized = response.status === 401;
      if (isUnauthorized && !isAfterUnauthorized) {
        nextPollMs = MANIFEST_RETRY_MS;
      } else if (response.status === 200 && response.data) {
        const next = response.data;
        shownStatus = 'ready';
        setState((current) => ({
          status: 'ready',
          manifest: pickManifest({
            current: current.status === 'ready' ? current.manifest : undefined,
            next,
            now: Date.now(),
          }),
        }));
      } else if (response.status === 202) {
        nextPollMs = MANIFEST_RETRY_MS;
        shownStatus = shownStatus === 'ready' ? 'ready' : 'preparing';
        setState((current) =>
          current.status === 'ready' ? current : { status: 'preparing' },
        );
      } else if (isUnauthorized || response.status === 404) {
        shownStatus = 'removed';
        setState({ status: 'removed' });
      } else {
        nextPollMs = MANIFEST_RETRY_MS;
        shownStatus = shownStatus === 'ready' ? 'ready' : 'unavailable';
        setState((current) =>
          current.status === 'ready' ? current : { status: 'unavailable' },
        );
      }

      isAfterUnauthorized = isUnauthorized;
      timeout = setTimeout(poll, nextPollMs);
    };

    poll();

    return () => {
      isActive = false;
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [api]);

  return state;
};

export default useWallboardManifest;
